import React, { FC, useEffect, useRef, useState } from 'react';
import {
  ArrowDownIcon,
  SearchFileIcon,
  ForbiddenIcon,
  CheckCircleOutlineIcon,
  CircleCloseIcon,
  PenIcon,
  SortingArrowIcon,
  TableButtonsIcon,
  ViewIcon,
} from '@src/svg';
import './table.scss';
import TableSkeleton from '@uikit/Loader/TableSkeleton/TableSkeleton';
import { handleImgError } from '@src/utils';
import {
  BIG_TABLE_DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE,
  FALLBACK_IMAGE,
  IMAGE_STORAGE_URL,
  SURVEY_TEMPLATE_DEFAULT_PAGE_SIZE,
} from '@src/utils/constants';
import { useAppDispatch } from '@store/hooks';
import TruncatedText from '@component/TruncatedText/TruncatedText';
import useIsSurveyTemplatesPage from '@hooks/useIsSurveyTemplatesPage';
import { TableProps } from './constants';
import CustomDropdown from '@uikit/CustomDropdown/CustomDropdown';
import { idType } from '@src/types';
import InfiniteScrollComponent from '@uikit/Pagination/InfiniteScroll';
import { Column } from '@uikit/Tables/types';
import { RenderRideStatusCell } from '@uikit/Tables/TableDynamic/RenderRideStatusCell';
import { useColumnDrag } from '@hooks/useColumnDrag';

const InfiniteTableDynamic: FC<TableProps> = ({
  isLoading = false,
  arrowIconMember = false,
  modalName,
  path,
  totalPages = 1,
  page = 0,
  totalElements,
  size,
  columns,
  onEditClick,
  onAuditClick,
  onImageClick,
  onCheckboxClick,
  onPageChange,
  className,
  abilities,
  activeRowId,
  handleSelectedRow,
  rows,
  onFieldClick,
  tableName,
  requestTablePageChange,
  openModal,
  tableCustomClassName,
  tableTitle,
  columnsSorting,
  sortingTable,
  setSortingTable,
  hasPagination = true,
  method,
  argument,
  params,
  skeletonProps,
  tableIsBig,
  statusSetter,
  scrollThreshold,
}) => {
  const dispatch = useAppDispatch();

  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const isResizingRef = useRef<boolean>(false);

  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [updatedPage, setPage] = useState<number>(page);
  const [columnOrder, setColumnOrder] = useState(columns);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isPopupVisible, setIsPopupVisible] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<idType>(activeRowId || null);
  const [hoveredCol, setHoveredCol] = useState<string | null>(null);
  const isFetchingMoreRef = useRef(false);

  const isSurveyTemplate = useIsSurveyTemplatesPage();

  const { getHeaderDragProps } = useColumnDrag(
    columnOrder,
    setColumnOrder,
    tableRef
  );

  useEffect(() => {
    setSelectedItem(activeRowId || null);
  }, [activeRowId]);

  useEffect(() => {
    setColumnOrder(columns);
  }, [columns]);

  useEffect(() => {
    setPage(page);
  }, [page]);

  const getInitialRowsPerPage = () =>
    tableTitle === 'Service Type'
      ? BIG_TABLE_DEFAULT_PAGE_SIZE
      : isSurveyTemplate
        ? SURVEY_TEMPLATE_DEFAULT_PAGE_SIZE
        : DEFAULT_PAGE_SIZE;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        tableContainerRef.current &&
        !tableContainerRef.current.contains(event.target as Node)
      ) {
        setIsPopupVisible(false);
      }
    };
    if (isPopupVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopupVisible]);

  useEffect(() => {
    const handleMouseUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false;
        setIsResizing(false);
      }
    };

    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  const hasTableData = rows && rows.length > 0;

  const handleSorting = (col: Column) => {
    if (method === 'GET' || !col.isSortable) {
      return;
    }

    setSortingTable((prevSorting) => {
      const newSortDir =
        prevSorting.sortBy === col.sortingField &&
        prevSorting.sortDir === 'desc'
          ? 'asc'
          : 'desc';

      const updatedSorting = {
        sortBy: col.sortingField,
        sortDir: newSortDir,
      };

      requestTablePageChange &&
        dispatch(
          requestTablePageChange.initiate(
            {
              requestId: Date.now(),
              id: 2,
              pathName: path,
              type: path,
              argument: argument ?? {},
              ...(argument ? argument : {}),
              params: {
                page,
                size,
                ...params,
                ...updatedSorting,
              },
              body: tableName ? columnsSorting[tableName] : {},
            },
            { forceRefetch: true }
          )
        );

      return updatedSorting;
    });
  };

  useEffect(() => {
    const excludedFields = ['viewImage', 'edit', 'view', 'audit'];
    const initialWidths = columns?.reduce((acc, col) => {
      if (excludedFields.includes(col.field)) {
        acc[col.field] = 60;
      } else {
        acc[col.field] = 160;
      }
      return acc;
    }, {});
    setColumnWidths(initialWidths);
  }, [columns]);

  const renderColField = ({
    field,
    type,
    row,
  }: {
    field: string;
    type: string;
    row: any;
  }) => {
    return (
      <>
        {type === 'clickableString' ? (
          <span
            className="table-clickable-field"
            onClick={() => onFieldClick?.(row, field)}
          >
            <TruncatedText text={row[field]} />
          </span>
        ) : (
          <TruncatedText text={row[field]} />
        )}
      </>
    );
  };

  const buttonsDropDownOptions = (
    rowId: idType,
    buttons: {
      view: boolean;
      edit: boolean;
      audit: boolean;
    }
  ) => [
    ...(abilities?.includes('Edit') && buttons.edit
      ? [
          {
            item: (
              <>
                <PenIcon width={16} height={16} /> Edit
              </>
            ),
            closeWhenClicked: true,
            onSelect: () => {
              openModal?.(modalName, rowId);
              onEditClick?.(rowId);
              setSelectedItem(rowId);
            },
          },
        ]
      : []),
    ...(abilities?.includes('Audit') && buttons.audit
      ? [
          {
            item: (
              <>
                <SearchFileIcon width={16} height={16} /> Audit
              </>
            ),
            closeWhenClicked: true,
            onSelect: () => onAuditClick?.(rowId, 'auditLog', tableName),
          },
        ]
      : []),
  ];

  const statusDropDownOptions = (id: idType, value: boolean) => [
    {
      item: <div className="status_item">Active</div>,
      closeWhenClicked: true,
      onSelect: () => {
        statusSetter && !value && statusSetter(id, true);
      },
    },
    {
      item: <div className="status_item">Inactive</div>,
      closeWhenClicked: true,
      onSelect: () => statusSetter && value && statusSetter(id, false),
    },
  ];

  return (
    <div className="infinite_scroll_table">
      <div
        className="infinite_scroll_table_container"
        data-test-id="infinite-table-dynamic-container"
      >
        <InfiniteScrollComponent
          dataLength={rows?.length || 0}
          currentPage={page || 0}
          totalPages={totalPages}
          isLast={page >= totalPages}
          getData={(nextPage: number) => {
            if (!requestTablePageChange) return;
            if (isFetchingMoreRef.current) return;
            if (Number.isFinite(totalPages) && nextPage >= totalPages) return;

            isFetchingMoreRef.current = true;
            dispatch(
              requestTablePageChange.initiate(
                {
                  requestId: Date.now(),
                  params: {
                    page: nextPage,
                    size,
                    ...params,
                    ...sortingTable,
                  },
                  pathName: path,
                  type: path,
                  id: 2,
                  argument: argument ?? {},
                  ...(argument ? argument : {}),
                  body: tableName ? columnsSorting[tableName] : {},
                },
                { forceRefetch: true }
              )
            ).finally(() => {
              isFetchingMoreRef.current = false;
            });
          }}
          loadingBlock={<TableSkeleton />}
          scrollableTarget="scrollableDiv"
          scrollThreshold={scrollThreshold}
        >
          <div
            ref={tableContainerRef}
            id="scrollableDiv"
            className={`table-container scrollable ${tableCustomClassName || ''}`}
          >
            {isLoading ? (
              <TableSkeleton {...skeletonProps} />
            ) : (
              <div className="custom-table-wrapper">
                <div className="t-scroll">
                  <table
                    ref={tableRef}
                    className={`custom-table ${isUpdating && 'blured'} ${className}`}
                  >
                    <thead className="thead-title">
                      <tr className="t-row">
                        {columnOrder?.map((col: Column, index: number) => {
                          const isSorted =
                            sortingTable?.sortBy === col.sortingField;
                          const isNonEditableColumn = [
                            'edit',
                            'audit',
                            'view',
                            'viewImage',
                          ].includes(col.field);
                          return (
                            <th
                              key={col.field}
                              data-test-id={`infinite-table-dynamic-head-th-${col.field}`}
                              className={`sortable-header ${col.className}`}
                              onClick={() => handleSorting(col)}
                              onMouseEnter={() => setHoveredCol(col.field)}
                              onMouseLeave={() => setHoveredCol(null)}
                              {...(!isNonEditableColumn
                                ? getHeaderDragProps(col, index)
                                : {})}
                            >
                              <div
                                className="drag-handle"
                                onMouseDown={(e) => {
                                  if (isResizing) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }
                                }}
                              >
                                {col.headerName}
                                {col.isSortable &&
                                method !== 'GET' &&
                                !isNonEditableColumn &&
                                (!arrowIconMember ||
                                  isSorted ||
                                  hoveredCol === col.field) ? (
                                  <SortingArrowIcon
                                    width={16}
                                    height={16}
                                    isAsc={
                                      isSorted
                                        ? sortingTable?.sortDir === 'asc'
                                          ? 'asc'
                                          : 'desc'
                                        : 'asc'
                                    }
                                  />
                                ) : null}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>

                    {hasTableData && (
                      <tbody>
                        {rows?.map((row, index: number) => {
                          const hasViewField = columnOrder?.some(
                            (col) => col.field === 'view'
                          );
                          return (
                            <tr
                              key={row.id}
                              className={`draggable-row ${selectedItem !== null && selectedItem === row.id ? 'active' : ''}`}
                              onClick={
                                hasViewField
                                  ? undefined
                                  : () =>
                                      handleSelectedRow &&
                                      handleSelectedRow(row.id)
                              }
                            >
                              {columnOrder?.map((col) => (
                                <td
                                  key={col.field}
                                  className={
                                    col.type === 'status' ||
                                    col.type === 'buttons'
                                      ? 'non_hidden'
                                      : ''
                                  }
                                >
                                  {col.field === 'viewImage' ? (
                                    <button
                                      onClick={() =>
                                        onImageClick?.(
                                          row[col.field],
                                          row.uploadedBy
                                        )
                                      }
                                      disabled
                                      className={`my-table-button ${!abilities?.includes('Document') ? 'btn_disable' : ''}`}
                                    >
                                      <ForbiddenIcon />
                                    </button>
                                  ) : col.field === 'view' ? (
                                    <div
                                      className="table-view-icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSelectedRow &&
                                          handleSelectedRow(row.id);
                                      }}
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      <ViewIcon />
                                    </div>
                                  ) : col.type === 'checkbox' ? (
                                    !!row[col.field] ? (
                                      <CheckCircleOutlineIcon />
                                    ) : (
                                      <CircleCloseIcon />
                                    )
                                  ) : col.type === 'buttons' ? (
                                    <CustomDropdown
                                      button={<TableButtonsIcon />}
                                      buttonClassName="table-buttons-trigger"
                                      options={buttonsDropDownOptions(
                                        row.id,
                                        col.buttons
                                      )}
                                    />
                                  ) : col.type === 'avatar' ? (
                                    <div className="table_avatar">
                                      {row?.[col.field]?.hasOwnProperty(
                                        'icon'
                                      ) ? (
                                        <div className="table_avatar_icon">
                                          {row?.[col.field]?.icon}
                                        </div>
                                      ) : (
                                        <div className="image_wrapper">
                                          <img
                                            alt="table avatar image"
                                            src={
                                              row?.[col.field]?.image
                                                ? `${IMAGE_STORAGE_URL}/${row?.[col.field].image}`
                                                : FALLBACK_IMAGE
                                            }
                                            onError={handleImgError}
                                          />
                                        </div>
                                      )}
                                      <div className="table_avatar_info">
                                        <div className="table_avatar_fullName">
                                          <TruncatedText
                                            text={`${row?.[col.field].firstName} ${row?.[col.field].lastName}`}
                                          />
                                        </div>
                                        <div className="table_avatar_relation">
                                          <h4>{row?.[col.field].relation}</h4>
                                        </div>
                                      </div>
                                    </div>
                                  ) : col.type === 'status' ? (
                                    <>
                                      {col.isPillDisabled ? (
                                        <>
                                          {row?.[col.field] ? (
                                            <div className="status-option-custom">
                                              <span
                                                className={`option-status ${row?.[col.field] === 'Active' ? 'active' : 'inactive'}`}
                                                style={{ color: 'white' }}
                                              >
                                                {row?.[col.field]}
                                              </span>
                                            </div>
                                          ) : (
                                            row?.[col.field]
                                          )}
                                        </>
                                      ) : (
                                        <CustomDropdown
                                          button={
                                            <div
                                              className={`${
                                                row?.[col.field]
                                                  ? 'active'
                                                  : 'inactive'
                                              } status_dropdown`}
                                            >
                                              {row?.[col.field]
                                                ? 'Active'
                                                : 'Inactive'}
                                              <ArrowDownIcon
                                                width={16}
                                                height={16}
                                              />
                                            </div>
                                          }
                                          options={statusDropDownOptions(
                                            row.id,
                                            row?.[col.field]
                                          )}
                                        />
                                      )}
                                    </>
                                  ) : col.field === 'rideStatus' ? (
                                    <RenderRideStatusCell row={row} />
                                  ) : (
                                    renderColField({
                                      field: col.field,
                                      type: col.type,
                                      row,
                                    })
                                  )}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    )}
                  </table>
                  {!hasTableData && (
                    <div className="table-no-rows no-data-found">
                      No Data Found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </InfiniteScrollComponent>
      </div>
      {!!rows?.length && (
        <div className="infinite-entries">{totalElements} entries</div>
      )}
    </div>
  );
};

export default InfiniteTableDynamic;
